import { QueryClient, useQuery, useQueryClient } from "react-query";
import { DocumentSnapshot } from "firebase/firestore";
import { firestore } from "@/services/firestore";
import { CustomerUpdate, CustomerUpdateStatus } from "@/types/v2";
import { useTotalCustomerUpdatesQuery } from "./useTotalCustomerUpdatesQuery";
import {
  buildCustomerUpdatesSearchOptions,
  normalizeCustomerUpdatesSorting,
  type CustomerUpdatesFilters,
} from "@/lib/customerUpdates";

interface UsePaginatedCustomerUpdatesParams {
  shopId: string;
  currentPage: number;
  pageSize: number;
  sortBy: string | null;
  sortOrder?: "asc" | "desc";
  enabled?: boolean;
  // Add filter parameters
  userId?: string;
  status?: CustomerUpdateStatus | "all";
  cardNumberFilter?: string; // Add cardNumber "starts with" filter
}

interface PaginatedCustomerUpdatesResult {
  docs: CustomerUpdate[];
  total: number;
  hasMore: boolean;
  currentPage: number;
  totalPages: number;
}

// Store cursor snapshots in a Map for efficient lookups
const cursorCache = new Map<string, DocumentSnapshot>();

export const usePaginatedCustomerUpdates = ({
  shopId,
  currentPage,
  pageSize,
  sortBy = "createdAt",
  sortOrder = "desc",
  enabled = true,
  userId,
  status,
  cardNumberFilter: inputCardNumberFilter,
}: UsePaginatedCustomerUpdatesParams) => {
  const cardNumberFilter = inputCardNumberFilter?.toUpperCase();
  const queryClient = useQueryClient();

  // Use the total customerUpdates query
  const { data: totalCustomerUpdates = 0, isLoading: isTotalLoading } =
    useTotalCustomerUpdatesQuery({
      shopId,
      userId,
      status: status === "all" ? undefined : status,
      cardNumberFilter,
      enabled,
    });

  return useQuery({
    queryKey: [
      "paginated-customerUpdates",
      shopId,
      currentPage,
      pageSize,
      sortBy,
      sortOrder,
      userId,
      status,
      cardNumberFilter,
    ],
    queryFn: async (): Promise<PaginatedCustomerUpdatesResult> => {
      // Generate cache key for cursor (include filters in key)
      const filterKey = `${userId || "all"}-${status || "all"}-${cardNumberFilter || "all"}`;
      const prevPageKey = `paginated-customerUpdates-${shopId}-${currentPage - 1}-${pageSize}-${sortBy}-${sortOrder}-${filterKey}`;
      const currentPageKey = `paginated-customerUpdates-${shopId}-${currentPage}-${pageSize}-${sortBy}-${sortOrder}-${filterKey}`;

      // Get cursor from previous page if not first page
      let startAfterDoc: DocumentSnapshot | undefined;
      if (currentPage > 1) {
        startAfterDoc = cursorCache.get(prevPageKey);

        // If cursor not in cache, we need to fetch previous pages first
        if (!startAfterDoc) {
          await buildCursorChain(
            shopId,
            currentPage,
            pageSize,
            sortBy ?? "",
            sortOrder,
            userId,
            status === "all" ? undefined : status,
            cardNumberFilter
          );
          startAfterDoc = cursorCache.get(prevPageKey);
        }
      }

      // Build search options for filters
      const filters: CustomerUpdatesFilters = {
        userId,
        status,
        cardNumberFilter,
      };
      const searchOptions = buildCustomerUpdatesSearchOptions(filters);
      const normalizedSorting = normalizeCustomerUpdatesSorting({
        sortBy,
        sortOrder,
      });

      // Use the existing subscribeCollectionWQueries pattern for filtering
      // Since we need pagination, we'll create a custom query
      let result;
      if (searchOptions.length > 0) {
        // For filtered queries, we'll need to use a different approach
        // since paginateDocuments doesn't support multiple where clauses
        result = await firestore.paginateDocumentsWithFilters<CustomerUpdate>(
          `/shops/${shopId}/customerUpdates`,
          pageSize,
          startAfterDoc,
          undefined, // converter
          normalizedSorting.sortBy ?? undefined,
          normalizedSorting.sortOrder,
          searchOptions
        );
      } else {
        // No filters, use existing method
        result = await firestore.paginateDocuments<CustomerUpdate>(
          `/shops/${shopId}/customerUpdates`,
          pageSize,
          startAfterDoc,
          undefined, // converter
          normalizedSorting.sortBy ?? undefined,
          normalizedSorting.sortOrder
        );
      }

      // Cache the cursor for this page
      if (result.lastVisible) {
        cursorCache.set(currentPageKey, result.lastVisible);
      }

      const totalPages = Math.ceil(totalCustomerUpdates / pageSize);

      return {
        docs: result.docs.map((customerUpdate) => ({
          ...customerUpdate,
        })) as CustomerUpdate[],
        total: totalCustomerUpdates, // Use total from useTotalCustomerUpdatesQuery
        hasMore: result.hasMore,
        currentPage,
        totalPages,
      };
    },
    enabled: enabled && !isTotalLoading, // Wait for total to load first
    staleTime: 10 * 1000,
    cacheTime: 10 * 1000,
    keepPreviousData: false,
    // Prefetch next page
    onSuccess: (data) => {
      if (data.hasMore) {
        queryClient.prefetchQuery({
          queryKey: [
            "paginated-customerUpdates",
            shopId,
            currentPage + 1,
            pageSize,
            sortBy,
            sortOrder,
            userId,
            status,
            cardNumberFilter,
          ],
          queryFn: async () => {
            const filterKey = `${userId || "all"}-${status || "all"}-${cardNumberFilter || "all"}`;
            const nextPageKey = `paginated-customerUpdates-${shopId}-${currentPage}-${pageSize}-${sortBy}-${sortOrder}-${filterKey}`;
            const startAfterDoc = cursorCache.get(nextPageKey);

            if (!startAfterDoc) return null;

            // Build search options for filters
            const filters: CustomerUpdatesFilters = {
              userId,
              status,
              cardNumberFilter,
            };
            const searchOptions = buildCustomerUpdatesSearchOptions(filters);
            const normalizedSorting = normalizeCustomerUpdatesSorting({
              sortBy,
              sortOrder,
            });

            let result;
            if (searchOptions.length > 0) {
              // For filtered queries, we'll need to use a different approach
              // since paginateDocuments doesn't support multiple where clauses
              result =
                await firestore.paginateDocumentsWithFilters<CustomerUpdate>(
                  `/shops/${shopId}/customerUpdates`,
                  pageSize,
                  startAfterDoc,
                  undefined, // converter
                  normalizedSorting.sortBy ?? undefined,
                  normalizedSorting.sortOrder,
                  searchOptions
                );
            } else {
              // No filters, use existing method
              result = await firestore.paginateDocuments<CustomerUpdate>(
                `/shops/${shopId}/customerUpdates`,
                pageSize,
                startAfterDoc,
                undefined, // converter
                normalizedSorting.sortBy ?? undefined,
                normalizedSorting.sortOrder
              );
            }

            return {
              docs: result.docs as CustomerUpdate[],
              total: totalCustomerUpdates, // Use cached total
              hasMore: result.hasMore,
              currentPage: currentPage + 1,
              totalPages: data.totalPages,
            };
          },
          staleTime: 1 * 30 * 1000,
        });
      }
    },
  });
};

// Helper function to build cursor chain for jumping to arbitrary pages
/**
 *
 * @param shopId
 * @param targetPage
 * @param pageSize
 * @param sortBy
 * @param sortOrder
 * @param userId
 * @param status
 * @param cardNumberFilter
 */
async function buildCursorChain(
  shopId: string,
  targetPage: number,
  pageSize: number,
  sortBy: string,
  sortOrder: "asc" | "desc",
  userId?: string,
  status?: CustomerUpdateStatus,
  cardNumberFilter?: string
) {
  let lastDoc: DocumentSnapshot | undefined;

  for (let page = 1; page < targetPage; page++) {
    const filterKey = `${userId || "all"}-${status || "all"}-${cardNumberFilter || "all"}`;
    const cacheKey = `paginated-customerUpdates-${shopId}-${page}-${pageSize}-${sortBy}-${sortOrder}-${filterKey}`;

    if (!cursorCache.has(cacheKey)) {
      const result = await firestore.paginateDocuments<CustomerUpdate>(
        `/shops/${shopId}/customerUpdates`,
        pageSize,
        lastDoc,
        undefined,
        sortBy,
        sortOrder
      );

      if (result.lastVisible) {
        cursorCache.set(cacheKey, result.lastVisible);
        lastDoc = result.lastVisible;
      }
    } else {
      lastDoc = cursorCache.get(cacheKey);
    }
  }
}

// Invalidate cursor cache, total, and paginated queries when customer updates change
export const invalidateCustomerUpdatesQueries = (
  queryClient: QueryClient,
  shopId?: string
) => {
  // Invalidate all paginated queries for this shop
  queryClient.invalidateQueries({
    queryKey: ["paginated-customerUpdates", shopId],
  });

  // Invalidate total customer updates query
  queryClient.invalidateQueries({
    queryKey: ["totalCustomerUpdates", shopId], // Fixed key to match the real one
  });

  // Clear the cursor cache whenever queries are invalidated
  cursorCache.clear();
};

// Use invalidation in your component or wherever you handle customer updates
export const useInvalidateCustomerUpdates = (shopId?: string) => {
  const queryClient = useQueryClient();

  return () => {
    invalidateCustomerUpdatesQueries(queryClient, shopId);
  };
};
